import React, { useMemo } from 'react';

import { CellContext, RowData } from '@tanstack/react-table';

import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { useCustomAttributes } from '.';
import {
  StatusCell,
  SpecCell,
  DateCell,
  PackageCell,
  CustomFieldCell,
} from '../components/cells';
import {
  MultipleAssigneeDisplay,
  ReviewersDisplay,
} from '../components/common';
import {
  SUBMITTALS_CUSTOM_ATTRIBUTES_FF,
  TABLE_COLUMN_ACCESSOR_KEY,
  TABLE_COLUMN_ID_KEY,
  CustomAttributesTypes,
} from '../consts';
import { useTranslation } from '../i18n';
import { Spec, SubmittalsItem, ReviewStep, Package } from '../types';

type CellParams = CellContext<RowData, never>;

export default function useTableColumns() {
  const { t } = useTranslation();
  const { value: isCustomReviewEnabled } = useAlloyFeatureFlag(
    'submittals-crw-fe',
    true,
  );

  const { value: isCustomAttributesEnabled } = useAlloyFeatureFlag(
    SUBMITTALS_CUSTOM_ATTRIBUTES_FF,
    true,
  );

  const { customAttributes, isLoading } = useCustomAttributes();
  const customFieldsColumns = useMemo(() => {
    if (!isCustomAttributesEnabled || isLoading || !customAttributes.length) {
      return [];
    }

    return customAttributes
      .toSorted((a, b) => a.name.localeCompare(b.name))
      .map((customField) => ({
        header: () => <OverflowTooltip>{customField.name}</OverflowTooltip>,
        accessorKey: customField.id,
        minSize: 120,
        size: 120,
        enableSorting: customField.type !== CustomAttributesTypes.MULTI_SELECT,
        sortDescFirst: false,
        cell: ({ row, column }: CellParams) => (
          <CustomFieldCell
            itemData={row?.original as SubmittalsItem}
            columnId={column?.id as string}
          />
        ),
      }));
  }, [customAttributes, isLoading, isCustomAttributesEnabled]);

  const reviewerColumns = useMemo(
    () => [
      {
        header: t('DATA_VIEW_COLUMN_REVIEWERS'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.STEPS,
        minSize: 120,
        size: 120,
        enableSorting: false,
        sortDescFirst: false,
        cell: ({ getValue }: CellParams) => (
          <div style={{ width: '100%' }}>
            <ReviewersDisplay
              reviewSteps={(getValue() as ReviewStep[]) || []}
              stacked
            />
          </div>
        ),
      },
    ],
    [t],
  );

  const tableColumns = useMemo(
    () => [
      {
        header: t('DATA_VIEW_COLUMN_STATUS'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.STATE_ID,
        minSize: 200,
        enableSorting: true,
        sortDescFirst: false,
        cell: ({ row, getValue }: CellParams) => (
          <StatusCell
            stateId={getValue()}
            itemData={row?.original as SubmittalsItem}
          />
        ),
      },
      {
        header: t('DATA_VIEW_COLUMN_CUSTOM_NUMBER'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.CUSTOM_IDENTIFIER,
        id: TABLE_COLUMN_ID_KEY.CUSTOM_IDENTIFIER,
        minSize: 100,
        size: 100,
        enableSorting: true,
        sortDescFirst: false,
        cell: ({ getValue }: CellParams) => (
          <OverflowTooltip>{`#${getValue()}`}</OverflowTooltip>
        ),
      },
      {
        header: t('DATA_VIEW_COLUMN_ID'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.IDENTIFIER,
        minSize: 55,
        size: 55,
        enableSorting: true,
        sortDescFirst: false,
      },
      {
        header: t('DATA_VIEW_COLUMN_SPEC'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.SPEC,
        id: TABLE_COLUMN_ID_KEY.SPEC,
        minSize: 120,
        size: 120,
        enableSorting: true,
        sortDescFirst: false,
        cell: ({ getValue }: CellParams) => (
          <SpecCell spec={getValue() as Spec} />
        ),
      },
      {
        header: t('DATA_VIEW_COLUMN_REV'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.REVISION,
        minSize: 60,
        size: 60,
        enableSorting: true,
        sortDescFirst: false,
        cell: ({ getValue }: CellParams) => (
          <OverflowTooltip>{getValue()}</OverflowTooltip>
        ),
      },
      {
        header: t('DATA_VIEW_COLUMN_TITLE'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.TITLE,
        minSize: 140,
        size: 140,
        enableSorting: false,
        sortDescFirst: false,
        cell: ({ getValue }: CellParams) => (
          <OverflowTooltip>{getValue()}</OverflowTooltip>
        ),
      },
      {
        header: t('DATA_VIEW_COLUMN_BALL_IN_COURT'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.BALL_IN_COURT_USERS,
        minSize: 120,
        size: 120,
        enableSorting: false,
        sortDescFirst: false,
        cell: ({ getValue, row }: CellParams) => (
          <MultipleAssigneeDisplay
            userIds={getValue() ?? []}
            itemData={row?.original as SubmittalsItem}
            stacked
          />
        ),
      },
      {
        header: t('DATA_VIEW_COLUMN_PENDING_ACTION_FROM'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.PENDING_BALL_IN_COURT,
        minSize: 120,
        size: 120,
        enableSorting: false,
        sortDescFirst: false,
        cell: ({ row }: CellParams) => (
          <MultipleAssigneeDisplay
            userIds={[]}
            itemData={row?.original as SubmittalsItem}
            stacked
            isPendingActionFrom
          />
        ),
      },
      {
        header: t('DATA_VIEW_COLUMN_DUE_DATE'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.DUE_DATE,
        minSize: 140,
        size: 140,
        enableSorting: true,
        sortDescFirst: false,
        cell: ({ getValue }: CellParams) => (
          <DateCell date={getValue() as Date} relative />
        ),
      },
      {
        header: t('DATA_VIEW_COLUMN_PRIORITY'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.PRIORITY,
        minSize: 90,
        size: 90,
        enableSorting: true,
        sortDescFirst: false,
      },
      {
        header: t('DATA_VIEW_COLUMN_PACKAGE'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.PACKAGE,
        id: TABLE_COLUMN_ID_KEY.PACKAGE,
        minSize: 100,
        size: 100,
        enableSorting: true,
        sortDescFirst: false,
        cell: ({ getValue }: CellParams) => (
          <PackageCell pkg={getValue() as Package} />
        ),
      },
    ],
    [t],
  );
  if (isCustomReviewEnabled) {
    return [...tableColumns, ...reviewerColumns, ...customFieldsColumns];
  } else {
    return tableColumns;
  }
}
